import React from 'react';
import InputTypesEnum from '../../../../Enums/InputTypesEnum';
import ValidityHelper from '../../../../Helpers/ValidityHelper';
import InvoiceAutomationService from '../../../../Services/InvoiceAutomationService';
import InvoiceAutomationItem from '../../../../Domain/ThirdParty/Project/InvoiceAutomationItem';
import ClientInvoiceReportOrderOptionsArray from '../../../../Enums/ClientInvoiceReportOrderOptionsArray';
import ClientInvoiceReportGroupOptionsArray from '../../../../Enums/ClientInvoiceReportGroupOptionsArray';
import {
  checkDaysToPay,
  checkLateFee,
  getInvoiceFormOptions,
  getOptionalChoiceOptions,
  getSetupToSave,
  isSuggestedValue,
} from '../../../../Helpers/invoiceAutomationEditorHelper';
import InvoiceAutomationSuggestedNote from './InvoiceAutomationSuggestedNote';

// A field's note while it holds the value a new setup took from the project's latest invoice; '' otherwise, which the
// wizard shows as no note.
const suggestionNote = (fieldName) => (setup) =>
  isSuggestedValue(setup, fieldName)
    ? React.createElement(InvoiceAutomationSuggestedNote, { invoiceId: setup.LatestTpClientInvoiceId })
    : '';

// The one invoice of a project's automatic invoicing, in the edit wizard's compact window, on the invoice form's two
// pages: who it goes to and its payment terms, then its details. Set Up opens it as the setup's first save, the fields
// filled in from the project's latest invoice (the AI suggestion) noted as such while they keep that value. As on the
// invoice form, the edited setup carries the client's contacts (ClientContacts), from which the bill-to contact's
// input shows the chosen contact's details; they are not saved.
export default class InvoiceAutomationWindowDescription {
  static titleForCreate = (setup) => `Set Up Automatic Invoicing – ${setup.ProjectName}`;
  static titleForEdit = (setup) => `Edit Monthly Invoice – ${setup.ProjectName}`;
  static get itemType() {
    return InvoiceAutomationItem;
  }
  static compact = true;
  static messageOnSuccess = 'Automatic invoicing has been saved.';
  static serviceFunction = (setup) => InvoiceAutomationService.UpsertInvoiceAutomation(getSetupToSave(setup));

  static pagesInGeneralEditWizzard = [
    {
      label: 'General',
      fields: [
        {
          fieldName: 'ContactId',
          label: 'Bill To Contact *',
          className: 'col-12',
          type: InputTypesEnum.InvoiceBillToContactSelect,
          validityChecker: ValidityHelper.forRequiredSelectField,
          idFilter: 'ClientId',
          info: suggestionNote('ContactId'),
        },
        {
          fieldName: 'CurrencyId',
          label: 'Currency *',
          className: 'col-12',
          type: InputTypesEnum.ActiveCurrencySelect,
          validityChecker: ValidityHelper.forRequiredSelectField,
          info: suggestionNote('CurrencyId'),
        },
        {
          fieldName: 'DaysToPay',
          label: 'Days To Pay *',
          className: 'col-12',
          type: InputTypesEnum.Number,
          validityChecker: checkDaysToPay,
          info: suggestionNote('DaysToPay'),
        },
        {
          fieldName: 'LatePaymentInterestRate',
          label: 'Late Fee % *',
          className: 'col-12',
          type: InputTypesEnum.NumberWithTwoDecimalPlaces,
          validityChecker: checkLateFee,
          info: suggestionNote('LatePaymentInterestRate'),
        },
      ],
    },
    {
      label: 'Invoice Details',
      fields: [
        {
          fieldName: 'ReferenceTemplate',
          label: 'Internal Reference Number',
          tooltipText: "The invoice's name.",
          placeholder: 'For example: {Month} {Year} Mystery Shops',
          maxLength: 100,
          className: 'col-12',
          type: InputTypesEnum.InvoiceReferenceTemplate,
          validityChecker: (value) => ValidityHelper.forMaximizedLengthField(value, 100),
          info: suggestionNote('ReferenceTemplate'),
        },
        {
          fieldName: 'Ponumber',
          label: 'Purchase Order #',
          placeholder: 'No PO',
          maxLength: 100,
          className: 'col-12',
          type: InputTypesEnum.Text,
          validityChecker: (value) => ValidityHelper.forMaximizedLengthField(value, 100),
          info: suggestionNote('Ponumber'),
        },
        {
          fieldName: 'DefaultReportName',
          label: 'Invoice Form *',
          className: 'col-12',
          type: InputTypesEnum.Select,
          validityChecker: ValidityHelper.forRequiredSelectField,
          selectvalues: getInvoiceFormOptions(),
          info: suggestionNote('DefaultReportName'),
        },
        {
          fieldName: 'ReportOrder',
          label: 'Form Order',
          className: 'col-12',
          type: InputTypesEnum.Select,
          validityChecker: ValidityHelper.AlwaysFlawless,
          selectvalues: getOptionalChoiceOptions(ClientInvoiceReportOrderOptionsArray),
          info: suggestionNote('ReportOrder'),
        },
        {
          fieldName: 'ReportGroup1',
          label: 'Form Group 1',
          className: 'col-12',
          type: InputTypesEnum.Select,
          validityChecker: ValidityHelper.AlwaysFlawless,
          selectvalues: getOptionalChoiceOptions(ClientInvoiceReportGroupOptionsArray),
          info: suggestionNote('ReportGroup1'),
        },
        {
          fieldName: 'ReportGroup2',
          label: 'Form Group 2',
          className: 'col-12',
          type: InputTypesEnum.Select,
          validityChecker: ValidityHelper.AlwaysFlawless,
          selectvalues: getOptionalChoiceOptions(ClientInvoiceReportGroupOptionsArray),
          info: suggestionNote('ReportGroup2'),
        },
        {
          fieldName: 'CommentTemplate',
          label: 'Invoice Comments',
          tooltipText: 'Printed on the invoice. {Month} and {Year} are filled in here too.',
          maxLength: 4000,
          className: 'col-12',
          type: InputTypesEnum.TextBox,
          validityChecker: (value) => ValidityHelper.forMaximizedLengthField(value, 4000),
          info: suggestionNote('CommentTemplate'),
        },
      ],
    },
  ];
}
